import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, extractApiError } from '../lib/api';
import { useAuth } from '../stores/auth.store';

interface Unit {
  id: string;
  name: string;
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const officer = useAuth((s) => s.officer);
  const setOfficer = useAuth((s) => s.setOfficer);

  const [firstName, setFirstName] = useState(officer?.firstName ?? '');
  const [lastName, setLastName] = useState(officer?.lastName ?? '');
  const [opdCardNo, setOpdCardNo] = useState(officer?.opdCardNo ?? '');
  const [phone, setPhone] = useState(officer?.phone ?? '');
  const [unitId, setUnitId] = useState<string>(officer?.unitId ?? '');
  const [units, setUnits] = useState<Unit[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get<Unit[]>('/app/units')
      .then((r) => setUnits(r.data))
      .catch(() => undefined);
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post('/app/auth/register', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        ...(opdCardNo.trim() ? { opdCardNo: opdCardNo.trim() } : {}),
        ...(unitId ? { unitId: Number(unitId) } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
      });
      setOfficer(data.officer);
      navigate('/pending', { replace: true });
    } catch (err) {
      setError(extractApiError(err).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto min-h-screen max-w-md bg-white p-4">
      <header className="mb-6 pt-4">
        <h1 className="text-2xl font-bold">ลงทะเบียนเจ้าหน้าที่ อปพร.</h1>
        <p className="mt-1 text-sm text-gray-500">
          กรอกข้อมูลให้ครบเพื่อส่งให้ผู้ดูแลพิจารณาอนุมัติ
        </p>
      </header>

      {officer?.linePictureUrl && (
        <div className="mb-4 flex items-center gap-3 rounded-2xl bg-gray-50 p-3">
          <img
            src={officer.linePictureUrl}
            alt=""
            className="h-12 w-12 rounded-full object-cover"
          />
          <div>
            <p className="text-sm font-medium">{officer.lineDisplayName}</p>
            <p className="text-xs text-gray-500">เชื่อมต่อ LINE แล้ว</p>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label">ชื่อ *</label>
          <input
            className="input"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            maxLength={128}
          />
        </div>
        <div>
          <label className="label">นามสกุล *</label>
          <input
            className="input"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            maxLength={128}
          />
        </div>
        <div>
          <label className="label">เลขบัตร อปพร.</label>
          <input
            className="input"
            value={opdCardNo}
            onChange={(e) => setOpdCardNo(e.target.value)}
            maxLength={64}
          />
        </div>
        <div>
          <label className="label">สังกัด / ชุดปฏิบัติการ</label>
          <select
            className="input"
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
          >
            <option value="">— เลือกสังกัด —</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">เบอร์โทรศัพท์</label>
          <input
            className="input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            inputMode="tel"
            pattern="0[0-9]{8,9}"
            placeholder="0812345678"
          />
        </div>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? 'กำลังส่ง...' : 'ส่งคำสมัคร'}
        </button>
      </form>
    </div>
  );
}
